import { Grid, useTheme } from "@mui/material";
import { api } from "api";
import { Activity, AllActivityTypes } from "api/types";
import { ActivityResults } from "components/ActivityResults";
import { ActivitySearchBar } from "components/ActivitySearchBar";
import { InfoSnackBar } from "components/InfoSnackBar";
import { PreviewSelectedActivity } from "components/PreviewSelectedActivity";
import { StyledButton } from "components/StyledButton";
import { ACTIVITIES_FETCH_ERR } from "messages";
import { ParamType } from "pages/AssignmentManagement/CreateAssignmentPage/CreateAssignmentPage";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

type Mode = "edit" | "create";

// Mode to determine if edit or create, and handler to
// pass up the current selected activities
export interface SearchActivitiesProps {
	mode: Mode;
	activitiesHandler: (activities: Activity[]) => void;
}

/**
 * Component to search for activities. Had a mode parameter that can be used
 * to differentiate between searching for activities for a new assignment
 * or existing assignment.
 */
export const SearchActivities = (props: SearchActivitiesProps): JSX.Element => {
	const { mode, activitiesHandler } = props;
	const theme = useTheme();
	const params = useParams<ParamType>();
	const [selected, setSelected] = useState<Activity[]>([]);
	const [results, setResults] = useState<Activity[]>([]);
	const [page, setPage] = useState(0);
	const [err, setErr] = useState<boolean>(false);
	const componentMounted = useRef(true);

	useEffect(() => {
		if (mode === "edit") {
			api
				.getAssignment(params.assignmentId)
				.then((res) => {
					if (res.status === 200) {
						setSelected(res.data.activities);
					}
				})
				.catch(() => setErr(true));
		}
		api
			.getActivities({
				activityType: [...AllActivityTypes],
			})
			.then((res) => {
				if (!componentMounted.current) {
					return;
				}

				if (res.status !== 200 || res.data === null) {
					setPage(-1);
					setErr(true);
					return;
				}

				setErr(false);

				// The following is a workaround for
				// https://linear.app/science-island/issue/SI-126

				if (res.data.length === 0) {
					setResults([]);
					return;
				}
				setResults(res.data);
			})
			.catch(() => setErr(true));

		return () => {
			componentMounted.current = false;
		};
	}, [mode, params.assignmentId]);

	// Sets the values for new queries and for paginated queries,
	// Updates the array with additional results
	const fetchResultHandler = (
		newlyFetchedActivities: Activity[],
		newQuery: boolean,
	): void => {
		if (newQuery) {
			setResults(newlyFetchedActivities);
			return;
		}
		setResults(results.concat(newlyFetchedActivities));
	};

	const CreateAssignment: JSX.Element = (
		<Grid
			container
			justifyContent="flex-end"
			style={{ paddingRight: theme.spacing(1) }}
		>
			<StyledButton
				variant="contained"
				color="primary"
				onClick={() => activitiesHandler(selected)}
				data-testid="crt-assign-btn"
			>
				{mode === "edit" ? "Update Assignment" : "Create Assignment"}
			</StyledButton>
		</Grid>
	);

	return (
		<Grid>
			<PreviewSelectedActivity
				show={true}
				selected={selected}
				handler={setSelected}
				mode={mode}
			/>
			{selected.length > 0 && CreateAssignment}
			<ActivitySearchBar
				handler={fetchResultHandler}
				page={page}
				setPage={setPage}
			/>
			<ActivityResults
				selected={selected}
				handler={setSelected}
				activities={results}
				page={page}
				setPage={setPage}
			/>
			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				severity="error"
				message={ACTIVITIES_FETCH_ERR}
			/>
		</Grid>
	);
};
